import type { Country, PublicCountryConfig } from '@tenders/shared';
import { publicCountryConfigs } from '@tenders/shared';

// The web imports the WEB-SAFE registry only. `publicCountryConfigs`
// (packages/shared/src/country/public-registry.ts) is built from `uz.public.ts`
// — the `PublicCountryConfig` subset with NO Node-dep fields — so the classifier
// (now a `CountryConfig` field backed by the ingestion keyword tables), the ИНН
// validator and the source-key list never enter the browser bundle.
//
// Resolved once at module load. VITE_COUNTRY is baked into the Vite build (Task 2),
// so in the browser and the SSR bundle `import.meta.env.VITE_COUNTRY` is a static
// literal. But this module is also pulled in by plain-`tsx` TOOLING — most notably
// `scripts/prerender.ts` (via `src/i18n/index.ts`, which derives LOCALES from the
// config) — where there is no Vite transform and `import.meta.env` is `undefined`.
// So resolve the country tolerantly: Vite's baked value first, then `process.env`
// (how prerender.ts itself reads VITE_COUNTRY), then 'uz'. We read ONLY this one
// variable rather than validating the whole web env through `parseWebEnv`, because
// resolving the active country must not require VITE_API_URL et al. to be present in
// a tooling context. The prod web build still fails loud on a missing VITE_COUNTRY —
// `vite.config.ts`'s assertProdEnv rejects it long before this runs.
//
// `VITE_COUNTRY` is typed `'uz' | 'kz' | 'es' | 'cl'` (`lib/env.ts`), the same list as
// `Country`/`COUNTRIES` in the shared registry.
const metaEnv = (import.meta as { env?: Record<string, string | undefined> }).env;
const fromProcess = typeof process !== 'undefined' ? process.env.VITE_COUNTRY : undefined;
const active = (metaEnv?.VITE_COUNTRY ?? fromProcess ?? 'uz') as Country;

// Fail loud on a value that names no config (a typo'd VITE_COUNTRY, or a kz build
// before a kz config exists) rather than silently handing out `undefined`.
if (!(active in publicCountryConfigs)) {
  throw new Error(
    `[country] VITE_COUNTRY='${active}' has no config — expected one of: ` +
      Object.keys(publicCountryConfigs).join(', '),
  );
}
export const countryConfig: PublicCountryConfig = publicCountryConfigs[active];
